import { api, mutate, readCache, type Ack } from '../api'
import { routeGen } from '../route-state'
import { TRADE_OPTIONS, type Trade } from '../trade'
import { clear, dayLabel, el, loadError, todayISO } from '../ui'
import { dayIn } from '../round-config'
import { sizeWords } from '../import-requests'
import { styleSelect } from '../styled-select'
import { offerUndo } from '../undo'
import { FIRST_100_PLACES, FREE_MONTHS, FREE_OFFER_WORDS, LIAMS_OWN_ROUNDS, type FreeOffer } from '../../functions/lib/free-time'
import { bandFor, poundsWords } from '../../functions/lib/plan'
import '../rounds.css'

// Rounds (admin only; first called Cleaners, renamed 2026-09-27): every business signed up to Roundly, how each is
// going, and what needs Liam. Redesigned 19 Oct 2026 (Liam: "ship this new designs", after option A at
// https://claude.ai/artifact/HjUAt49yaLrKdGXVEzi5ss and reviews by Sol, Sonnet and Astra, docs/plans/rounds-page/):
//   - one line on the outside rounds and four numbers about them (Liam's own rounds never count);
//   - Needs you: Help questions sent to him, customer files to put in, maybe repeats, rounds that stopped or never
//     got started; late ones first, then the longest waiting; each answer moves it to Done (0076), with Undo;
//   - every outside round on one line, "How it's going" from the work it saves; a round opens beside the list (on a
//     phone, full screen with Back) with its facts and switches. Yours sit at the foot, out of every number.
// Creator applications have their own Partners page.

type BusinessStatus = 'trial' | 'active' | 'suspended' | 'closed'

interface AdminBusinessRow {
  id: number
  name: string
  // An owner closed it and asked for it to be deleted (functions/api/me/account.ts), when.
  delete_asked_on?: string | null
  status: BusinessStatus
  signed_up_on: string
  last_seen_on: string | null
  people: { email: string; role: string }[]
  counts: { customers: number; visits: number; days_rostered: number }
  // 0028: the kind of round and, for a trial, the last free day.
  trade?: Trade
  trial_ends_on?: string | null
  // 0033: the tag on the website link they signed up from ("first-100"), or null.
  signup_from?: string | null
  // 0066: which free time the round has, or null for a round from before free months (Dropby).
  free_offer?: FreeOffer | null
  // Holds a First 100 place, kept for good (a tester who came through the link still holds one).
  first_100_place?: boolean
  // How much it is used: of the last four 7-day weeks, how many had work saved, whether this one did, and whole days
  // since the last work saved (null: none in the 28 days).
  use?: { weeks: number; this_week: boolean; days_since?: number | null }
  // Where the round is (0034): GB, US or CA. Only UK rounds are ever charged.
  country?: string
  // Active customers by the terms (a visit done in the last 12 months), which set its price (billing stage 2).
  active_customers?: number
  // Visits ticked done (never imported) in the last 7 days, and the last one's day.
  jobs_done_7d?: number
  last_job_done_on?: string | null
  // PATCH needs it, so the status control stays off until a row carries one.
  version?: number
  // Several jobs per customer (0042, Liam Q13): on or off for this round, the settings version its switch needs,
  // and whether the round already has jobs (then it can no longer go off).
  several_jobs?: { on: boolean; version: number | null; in_use: boolean }
  // Money brought from Squeegee (0043): the same shape. Owners turn it on by accepting the money check on an import;
  // this switch is Liam's, for testing.
  brought_money?: { on: boolean; version: number | null; in_use: boolean }
  // Maybe a repeat of an earlier round (free months once per business: flagged for Liam, never refused).
  repeat_of?: { id: number; name: string; why: ('name' | 'phone' | 'customers')[]; shared: number }
  // A partner's own round (billing stage 8b): the partner code linked, and the free year once given.
  partner?: { code: string; given_at: string | null; free_until: string | null }
  // Emailed enquiries sent on by postcode (0077): the round and areas, the settings version, and the rounds with an
  // owner in common it could send them to.
  route?: { to: number | null; areas: string | null; version: number | null; choices: { id: number; name: string }[] }
}

// Liam's answer on a Needs you item that is not a Help question or a file (0076).
interface Followup {
  id: number
  business_id: number
  kind: 'repeat' | 'stopped' | 'not_started'
  item_key: string
  outcome: string
  done_at: string
}

const STATUS_LABEL: Record<BusinessStatus, string> = {
  trial: 'Trial',
  active: 'Active',
  suspended: 'Paused',
  closed: 'Closed',
}
const STATUS_VALUES: BusinessStatus[] = ['trial', 'active', 'suspended', 'closed']
// What a status change does, said before it is made (Astra, 19 Oct 2026: Paused shuts everyone out, which the payment
// pause does not; functions/api/_middleware.ts refuses a paused or closed round everything).
const STATUS_ASK: Record<BusinessStatus, (name: string) => string> = {
  trial: (n) => `Put ${n} back on Trial?`,
  active: (n) => `Mark ${n} as Active?`,
  suspended: (n) => `Pause ${n}? Nobody at ${n} can open Roundly until you set it back. Their customers and history are kept. This is not the pause for an unpaid bill.`,
  closed: (n) => `Close ${n}? Nobody at ${n} can open Roundly. Their customers and history are kept, and you can set it back.`,
}

let requestSequence = 0
let rows: AdminBusinessRow[] = []
let followups: Followup[] = []

// The rounds list: one filter chip at a time, the search, the sort, how many shown, and the round open beside it.
type Going = 'new' | 'every' | 'some' | 'stopped' | 'not-started'
type RoundsFilter = 'all' | Going | 'free' | 'ending' | 'first-100' | 'tester' | 'referred' | 'paused'
type SortKey = 'newest' | 'last-job' | 'free-ending' | 'most-active'
let filter: RoundsFilter = 'all'
let query = ''
let sort: SortKey = 'newest'
let roundsShown = 25
let selected: number | null = null
// Where the page was when a round opened, so Back (a phone) returns there and to the round's own name.
let scrollBeforeOpen = 0
let pendingChange: { id: number; status: BusinessStatus } | null = null

// A saved time (UTC) shown as its day in London, where Liam reads it; a day on its own (a last free day) shown as
// itself, never moved by the browser's clock (Astra: 12 Apr read as 11 Apr in New York).
function dateLabel(v: string | null | undefined): string {
  if (!v) return 'never'
  const timed = v.includes('T') || v.includes(' ')
  const d = new Date(timed ? v.replace(' ', 'T') + (v.endsWith('Z') ? '' : 'Z') : `${v}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return v
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: timed ? 'Europe/London' : 'UTC' })
}
// The same, shorter: "17 Oct", with the year only when it is not this one.
function shortDate(v: string | null | undefined): string {
  if (!v) return 'never'
  const full = dateLabel(v)
  const year = todayISO().slice(0, 4)
  return full.endsWith(` ${year}`) ? full.slice(0, -5) : full
}
// A saved UTC time's day in London ("2026-10-06" for 23:30 UTC on 5 Oct in summer).
function londonDay(v: string): string {
  const d = new Date(v.includes(' ') || v.includes('T') ? v.replace(' ', 'T') + (v.endsWith('Z') ? '' : 'Z') : `${v}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? v.slice(0, 10) : dayIn('Europe/London', d)
}
const savedTime = (v: string): number => new Date(`${v.replace(' ', 'T')}${v.endsWith('Z') ? '' : 'Z'}`).getTime()

function showFailure(ack: Ack, err: HTMLElement, root: HTMLElement): void {
  if (ack.status === 409) {
    err.textContent = 'Someone else changed this just now. Reloading.'
    void load(root)
    return
  }
  if (ack.status >= 400 && ack.status < 500) {
    err.textContent = ack.error ?? "That didn't save."
    return
  }
  err.textContent = "Couldn't save yet. Check your connection. It will keep trying."
}

// "Came from first-100" (Liam, 29 Sep 2026: see who signs up, and from where). No
// tag reads "Came from direct"; rounds from before the tag was kept say nothing,
// since nobody knows (Dropby, and the Enfield trial of 28 Sep).
const TAGS_KEPT_FROM = '2026-09-29'
export function cameFrom(row: Pick<AdminBusinessRow, 'signup_from' | 'signed_up_on'>): string {
  if (row.signup_from) return `Came from ${row.signup_from}`
  return row.signed_up_on && row.signed_up_on.slice(0, 10) >= TAGS_KEPT_FROM ? 'Came from direct' : ''
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`
const daysFrom = (from: string, to: string): number =>
  Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / 86_400_000)
// Apple's made-up review round (Brightside Windows, appreview@roundly.work, 15 Oct 2026) is ours too, not a sign-up:
// Liam, 19 Oct 2026, after its £9.99 read like a paying round. It sits under Yours and out of the numbers along the top.
const APPLE_REVIEW_ROUND = 4
const isOwnId = (id: number): boolean => LIAMS_OWN_ROUNDS.includes(id) || id === APPLE_REVIEW_ROUND
const isOwn = (row: Pick<AdminBusinessRow, 'id'>): boolean => isOwnId(row.id)
// On its free months: a trial round whose last free day has not gone.
const onFreeMonths = (row: AdminBusinessRow, today: string): boolean =>
  row.status === 'trial' && !!row.trial_ends_on && daysFrom(today, row.trial_ends_on) >= 0
const endingSoon = (row: AdminBusinessRow, today: string): boolean => {
  if (row.status !== 'trial' || !row.trial_ends_on) return false
  const left = daysFrom(today, row.trial_ends_on)
  return left >= 0 && left <= 30
}
const working = (row: Pick<AdminBusinessRow, 'status'>): boolean => row.status === 'trial' || row.status === 'active'

// How it's going, one tag per round (Liam, 19 Oct 2026: kept the five), from the work it saves (Sonnet and Astra: a
// round busy planning is not "Stopped", so jobs alone never decide it):
//   New this week   signed up in the last 7 days;
//   Every week      work saved in each of the last 4 weeks (the price list's number to watch);
//   Stopped         has ticked jobs done, none for 14 days, and no work saved for 14 days;
//   Not started     no job ticked done yet, and no work saved for 7 days;
//   Some weeks      the rest: working, not every week.
// A paused or closed round has none (its status says it; Sonnet: "Stopped" beside Closed made the counts disagree), and
// Liam's own rounds are never Stopped or Not started (Sol).
export function going(row: Pick<AdminBusinessRow, 'id' | 'status' | 'signed_up_on' | 'use' | 'last_job_done_on'>, today: string = todayISO()): Going | null {
  if (!working(row)) return null
  if (daysFrom(londonDay(row.signed_up_on), today) < 7) return 'new'
  if ((row.use?.weeks ?? 0) >= 4) return 'every'
  const since = row.use?.days_since ?? null
  const quietFor = (days: number): boolean => since === null || since >= days
  if (!isOwn(row)) {
    if (row.last_job_done_on) {
      if (daysFrom(row.last_job_done_on, today) >= 14 && quietFor(14)) return 'stopped'
    } else if (quietFor(7)) return 'not-started'
  }
  return 'some'
}
// The tag drawn for a round: how it's going, or its status when paused or closed.
function goingTag(row: AdminBusinessRow, today: string): HTMLElement {
  const tag = going(row, today)
  return tag ? el('span', { class: `rounds-tag rounds-tag-${tag}` }, GOING_WORDS[tag]) : el('span', { class: 'rounds-tag rounds-tag-not-started' }, STATUS_LABEL[row.status])
}
const GOING_WORDS: Record<Going, string> = { new: 'New this week', every: 'Every week', some: 'Some weeks', stopped: 'Stopped', 'not-started': 'Not started' }

// What a round would pay a month at today's size (the price list's band for its active customers), once charging is on.
// Only UK rounds on free months or active count towards the top's total; a tester or a partner's free year pays later;
// a round outside the UK is never charged (the price list is pounds); a paused or closed one pays nothing.
function wouldPay(row: AdminBusinessRow): { words: string; now: number; later: number } {
  const pence = bandFor(row.active_customers ?? 0).pence
  if (row.country && row.country !== 'GB') return { words: `Not charged (${row.country})`, now: 0, later: 0 }
  if (!working(row)) return { words: '-', now: 0, later: 0 }
  // A tester or a partner's free year pays later, only while that free time lasts (Sol: an ended one pays like any other).
  const today = todayISO()
  const testerTime = row.free_offer === 'tester' && row.status === 'trial' && !!row.trial_ends_on && daysFrom(today, row.trial_ends_on) >= 0
  const partnerYear = !!row.partner?.given_at && !!row.partner.free_until && daysFrom(today, row.partner.free_until) >= 0
  if (testerTime || partnerYear) return { words: `${poundsWords(pence)} later`, now: 0, later: pence }
  return { words: poundsWords(pence), now: pence, later: 0 }
}

// About 10 invited testers (price list, 12 Oct 2026).
const TESTER_PLACES = 10

function renderHead(): HTMLElement {
  const today = todayISO()
  const outside = rows.filter((r) => !isOwn(r))
  const first100 = outside.filter((r) => r.first_100_place).length
  const head = el('div', { class: 'rounds-head' }, el('h2', {}, 'Rounds'))
  if (outside.length === 0) {
    const left = FIRST_100_PLACES - first100
    head.append(el('p', { class: 'rounds-lead' }, `No outside rounds yet. ${left === FIRST_100_PLACES ? `All ${FIRST_100_PLACES}` : `${left} of ${FIRST_100_PLACES}`} First 100 places are still free.`))
    return head
  }
  const tags = outside.map((r) => going(r, today))
  const didJobs = outside.filter((r) => (r.jobs_done_7d ?? 0) > 0).length
  const stopped = tags.filter((t) => t === 'stopped').length
  const notStarted = tags.filter((t) => t === 'not-started').length
  const quiet = [stopped ? `${stopped} ${stopped === 1 ? 'has' : 'have'} stopped` : '', notStarted ? `${notStarted} never got started` : ''].filter(Boolean)
  head.append(el('p', { class: 'rounds-lead' }, [`${plural(outside.length, 'outside round')}.`, `${didJobs} did jobs this week.`, quiet.length ? `${quiet.join(' and ')}.` : ''].filter(Boolean).join(' ')))
  return head
}

// The numbers along the top (Liam, 12 Oct 2026: "including analytics bar at the top"), about outside rounds only.
function renderSummary(): HTMLElement {
  const today = todayISO()
  const outside = rows.filter((r) => !isOwn(r))
  if (outside.length === 0) {
    return el('div', { class: 'rounds-empty' }, 'When the first round signs up, this shows who did jobs this week, who uses Roundly every week, and what they would pay once charging is on.')
  }
  const tile = (label: string, value: string, note: string, key = false): HTMLElement =>
    el('div', { class: `rounds-kpi${key ? ' rounds-kpi-key' : ''}` }, el('span', { class: 'rounds-kpi-label' }, label), el('strong', { class: 'rounds-kpi-value' }, value), el('span', { class: 'rounds-kpi-note' }, note))
  const fresh = outside.filter((r) => going(r, today) === 'new').length
  const didJobs = outside.filter((r) => (r.jobs_done_7d ?? 0) > 0).length
  const every = outside.filter((r) => (r.use?.weeks ?? 0) >= 4).length
  const pay = outside.map(wouldPay)
  const now = pay.reduce((n, p) => n + p.now, 0)
  const later = pay.reduce((n, p) => n + p.later, 0)
  const first100 = outside.filter((r) => r.first_100_place).length
  const testers = outside.filter((r) => r.free_offer === 'tester').length
  const ending = outside.filter((r) => endingSoon(r, today)).length
  const wrap = el('div', { class: 'rounds-summary' })
  wrap.append(
    el(
      'div',
      { class: 'rounds-kpis', role: 'group', 'aria-label': 'Outside rounds at a glance' },
      tile('Outside rounds', String(outside.length), fresh ? `${fresh} new this week` : 'none new this week'),
      tile('Did jobs this week', `${didJobs} of ${outside.length}`, 'a visit done on one of the last 7 days'),
      tile('Used it every week', String(every), 'work saved in each of the last 4 weeks: the ones most likely to pay', true),
      tile('Would pay a month, at today\'s size', poundsWords(now), `UK rounds, not testers or partner free years${later ? ` (${poundsWords(later)} later)` : ''}. Charging is off.`),
    ),
    el('p', { class: 'rounds-kpis-note' }, `First 100 places: ${first100} of ${FIRST_100_PLACES} taken · Testers: ${testers} of ${TESTER_PLACES} · Free time ending in the next 30 days: ${ending || 'none'}`),
  )
  return wrap
}

// ---- Needs you --------------------------------------------------------------------------------------------------

// Help questions (help.ts / functions/api/admin/help-questions.ts): the ones sent to Liam, and the ones Help could not
// answer that nobody sent (the answers to write next).
interface HelpQuestionRow {
  id: number
  business_id: number
  business_name: string
  role: string
  question: string
  answer_id: string | null
  outcome: string
  sent_to_us: number
  helpful: number | null
  created_at: string
  // Support phase 1 (0035): who asked, and for one sent to Liam the reply-by time and his Replied tick.
  asked_by?: string
  sent_at?: string | null
  reply_by?: string | null
  replied_at?: string | null
  // 0067: what they added under "Add more detail or a screenshot", and how many screenshots came with it.
  detail?: string | null
  screenshots?: number
}
// Customer files to put in (0039; Liam 1 Oct 2026: done for them "within 2 working days").
interface ImportRequestRow {
  id: number
  business_id: number
  business_name: string
  file_name: string
  size: number
  note: string | null
  status: 'waiting' | 'done'
  sent_by: string | null
  created_at: string
  due_on: string
  done_at: string | null
  // The file was deleted 48 hours after Mark put in (0076); its name stays.
  file_gone?: number | boolean
}
// One line on Done (GET /api/admin/done).
interface DoneItem {
  kind: 'help' | 'file' | 'repeat' | 'stopped' | 'not_started' | 'delete'
  id: number
  business_id: number
  business_name: string
  text: string
  who: string | null
  role: string | null
  outcome: string
  done_at: string
  // An answer's item (0076), which Put back names with its number.
  item_key: string | null
}

type NeedKind = 'help' | 'file' | 'repeat' | 'stopped' | 'not_started' | 'delete'
const KIND_WORDS: Record<NeedKind, string> = { help: 'Help question', file: 'Customer file', repeat: 'Maybe a repeat', stopped: 'Stopped', not_started: 'Not started', delete: 'Asked to delete' }
const KIND_CHIPS: { value: NeedKind | 'all' | 'yours'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'help', label: 'Help questions' },
  { value: 'file', label: 'Customer files' },
  { value: 'repeat', label: 'Maybe repeats' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'not_started', label: 'Not started' },
  { value: 'delete', label: 'Asked to delete' },
  { value: 'yours', label: 'From your rounds' },
]
const OUTCOME_WORDS: Record<string, string> = {
  replied: 'Replied',
  put_in: 'Put in',
  same_business: 'Same business',
  different_business: 'Different business',
  emailed: 'Emailed',
  not_needed: 'Not needed',
}

let helpRows: HelpQuestionRow[] | null = null
let helpFailed = false
let fileRows: ImportRequestRow[] | null = null
let filesFailed = false
let needsTab: 'todo' | 'done' = 'todo'
let needsKind: NeedKind | 'all' | 'yours' = 'all'
let needsShown = 5
let openHelp: number | null = null
// "Their Help questions waiting" on an open round shows only that round's items until Show all.
let needsRound: number | null = null
let showUnanswered = false
let doneItems: DoneItem[] = []
let doneNext: string | null = null
let doneQuery = ''
let doneState: 'idle' | 'loading' | 'failed' = 'idle'
// Set when "Show older" could not load: the list stays, with a Try again.
let doneMoreFailed = false
let doneSequence = 0
let doneTimer: number | null = null

interface Need {
  key: string
  kind: NeedKind
  roundId: number
  roundName: string
  yours: boolean
  // 3 late, 2 due within a day, 1 due later, 0 no date; then the due time, then the oldest first.
  urgency: number
  dueAt: number | null
  dueText: string
  since: number
  help?: HelpQuestionRow
  file?: ImportRequestRow
  round?: AdminBusinessRow
}

// "Reply by 3:40pm Tue 29 Sep", or "Overdue: was due 3:40pm Tue 29 Sep", in UK time (Liam's hours).
const UK_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const UK_STAMP = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
function replyByLine(q: HelpQuestionRow, now: Date = new Date()): { text: string; late: boolean; at: number } | null {
  if (!q.sent_to_us || q.replied_at || !q.reply_by) return null
  const at = new Date(`${q.reply_by.replace(' ', 'T')}Z`)
  if (Number.isNaN(at.getTime())) return null
  const parts = UK_STAMP.formatToParts(at)
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? ''
  const when = `${get('hour')}:${get('minute')}${get('dayPeriod').toLowerCase().replace(/[^a-z]/g, '')} ${get('weekday').replace('.', '')} ${get('day')} ${UK_MONTHS[Number(get('month')) - 1] ?? ''}`
  const late = at.getTime() < now.getTime()
  return { text: late ? `Overdue: was due ${when}` : `Reply by ${when}`, late, at: at.getTime() }
}

const answered = (kind: Followup['kind'], key: string): Followup | undefined => followups.find((f) => f.kind === kind && f.item_key === key)
const stoppedKey = (row: AdminBusinessRow): string => `${row.id}:${row.last_job_done_on ?? 'never'}`

// Everything waiting for Liam, in the order he should take it.
function buildNeeds(): Need[] {
  const now = Date.now()
  const today = todayISO()
  const out: Need[] = []
  for (const q of helpRows ?? []) {
    if (!q.sent_to_us || q.replied_at) continue
    const due = replyByLine(q)
    const urgency = !due ? 0 : due.late ? 3 : due.at - now < 86_400_000 ? 2 : 1
    out.push({ key: `help:${q.id}`, kind: 'help', roundId: q.business_id, roundName: q.business_name, yours: isOwnId(q.business_id), urgency, dueAt: due?.at ?? null, dueText: due?.text ?? '', since: savedTime(q.sent_at ?? q.created_at), help: q })
  }
  for (const f of fileRows ?? []) {
    if (f.status !== 'waiting') continue
    const late = f.due_on < today
    const urgency = late ? 3 : f.due_on === today ? 2 : 1
    out.push({ key: `file:${f.id}`, kind: 'file', roundId: f.business_id, roundName: f.business_name, yours: isOwnId(f.business_id), urgency, dueAt: Date.parse(`${f.due_on}T17:00:00Z`), dueText: late ? `Overdue: was due ${dayLabel(f.due_on)}` : f.due_on === today ? 'Due today' : `Due ${dayLabel(f.due_on)}`, since: savedTime(f.created_at), file: f })
  }
  for (const r of rows) {
    // Delete my account (4 Nov 2026): an owner closed their round and asked for it to be deleted. It stays here until the
    // round is deleted (docs/PRIVACY-REQUESTS.md section 12) or opened again.
    if (r.delete_asked_on) out.push({ key: `delete:${r.id}`, kind: 'delete', roundId: r.id, roundName: r.name, yours: isOwn(r), urgency: 2, dueAt: null, dueText: '', since: savedTime(r.delete_asked_on), round: r })
    if (r.repeat_of && !answered('repeat', `${r.id}:${r.repeat_of.id}`)) {
      out.push({ key: `repeat:${r.id}`, kind: 'repeat', roundId: r.id, roundName: r.name, yours: isOwn(r), urgency: 0, dueAt: null, dueText: '', since: savedTime(r.signed_up_on), round: r })
    }
    // Quiet rounds: outside rounds still working, never Liam's own, and never asked about twice for the same stop.
    if (isOwn(r) || !working(r)) continue
    const tag = going(r, today)
    if (tag === 'stopped' && !answered('stopped', stoppedKey(r))) {
      out.push({ key: `stopped:${r.id}`, kind: 'stopped', roundId: r.id, roundName: r.name, yours: false, urgency: 0, dueAt: null, dueText: '', since: Date.parse(`${r.last_job_done_on}T12:00:00Z`), round: r })
    } else if (tag === 'not-started' && !answered('not_started', String(r.id))) {
      out.push({ key: `not_started:${r.id}`, kind: 'not_started', roundId: r.id, roundName: r.name, yours: false, urgency: 0, dueAt: null, dueText: '', since: savedTime(r.signed_up_on), round: r })
    }
  }
  return out.sort((a, b) => b.urgency - a.urgency || (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity) || a.since - b.since)
}

function waitedWords(since: number): string {
  const mins = Math.max(0, Math.round((Date.now() - since) / 60_000))
  if (mins < 60) return mins < 2 ? 'just now' : `${mins} minutes`
  const hours = Math.round(mins / 60)
  if (hours < 24) return plural(hours, 'hour')
  return plural(Math.round(hours / 24), 'day')
}

function needWords(n: Need): { title: string; detail: string } {
  if (n.help) {
    const who = n.help.asked_by ? `${n.help.asked_by} (${n.help.role})` : n.help.role
    const shots = n.help.screenshots ? ` + ${plural(n.help.screenshots, 'screenshot')}` : ''
    return { title: `asked: “${n.help.question}”${shots}`, detail: who }
  }
  if (n.file) return { title: `sent their customer list: ${n.file.file_name}`, detail: `${sizeWords(n.file.size)}${n.file.sent_by ? `, from ${n.file.sent_by}` : ''}${n.file.note ? `. “${n.file.note}”` : ''}` }
  const r = n.round!
  if (n.kind === 'delete') return { title: 'closed their round and asked for it to be deleted', detail: `asked ${shortDate(r.delete_asked_on!.slice(0, 10))}. Email them their copy, then delete it (docs/PRIVACY-REQUESTS.md section 12)` }
  if (n.kind === 'repeat') return { title: repeatWords(r.repeat_of!), detail: '' }
  if (n.kind === 'stopped') return { title: `has done no jobs for ${plural(daysFrom(r.last_job_done_on!, todayISO()), 'day')}`, detail: `last job done ${shortDate(r.last_job_done_on)}, ${plural(r.active_customers ?? 0, 'active customer')}` }
  return { title: 'has done no jobs yet', detail: `signed up ${shortDate(r.signed_up_on)}, ${plural(r.counts.customers, 'customer')}${r.last_seen_on ? `, last signed in ${shortDate(r.last_seen_on)}` : ''}` }
}

// The line Liam saw, kept with his answer for the Done list.
function followupTitle(n: Need): string {
  const w = needWords(n)
  return `${n.roundName} ${w.title}`.slice(0, 300)
}

function ownerEmail(roundId: number): string | null {
  const r = rows.find((x) => x.id === roundId)
  return r?.people.find((p) => p.role === 'owner')?.email ?? r?.people[0]?.email ?? null
}

// One answer: send it, take the item off To do, and offer Undo.
function answer(root: HTMLElement, n: Need, outcome: string, button: HTMLButtonElement, err: HTMLElement): void {
  button.disabled = true
  err.textContent = ''
  const gen = routeGen
  const done = `${OUTCOME_WORDS[outcome] ?? outcome}: ${n.roundName}. Moved to Done.`
  // No signal: the save waits on this computer and goes when the connection is back (src/api.ts), so say that rather
  // than "didn't save", and leave the button off so it is not sent twice (Sonnet).
  const fail = (ack: Ack): void => {
    if (gen !== routeGen) return
    if (ack.status === 0) { err.textContent = 'No connection. It will send when you are back online.'; return }
    button.disabled = false
    err.textContent = ack.error ?? "That didn't save. Try again."
  }
  // An Undo after the page has moved on changes nothing on the screen now showing; Done is read again when next opened.
  const undone = (): void => { if (gen !== routeGen) return; doneChanged(); if (needsTab === 'done') loadDone(root); redrawNeeds(root) }
  if (n.help) {
    const q = n.help
    void mutate({ label: `Help question replied: ${n.roundName}`, method: 'PATCH', path: `/api/admin/help-questions/${q.id}`, body: { replied: true } }).then((ack) => {
      if (!ack.ok) { fail(ack); return }
      if (gen !== routeGen) return
      q.replied_at = new Date().toISOString().replace('T', ' ').slice(0, 19)
      afterAnswer(root, n)
      offerUndo(done, () => {
        void mutate({ label: `Help question not replied: ${n.roundName}`, method: 'PATCH', path: `/api/admin/help-questions/${q.id}`, body: { replied: false } }).then((back) => {
          if (back.ok) { q.replied_at = null; undone() }
        })
      })
    })
    return
  }
  if (n.file) {
    const f = n.file
    void mutate({ label: `Customer file put in: ${n.roundName}`, method: 'PATCH', path: `/api/admin/import-requests/${f.id}`, body: { done: true } }).then((ack) => {
      if (!ack.ok) { fail(ack); return }
      if (gen !== routeGen) return
      f.status = 'done'
      afterAnswer(root, n)
      offerUndo(done, () => {
        void mutate({ label: `Customer file not put in: ${n.roundName}`, method: 'PATCH', path: `/api/admin/import-requests/${f.id}`, body: { done: false } }).then((back) => {
          if (back.ok) { f.status = 'waiting'; undone() }
        })
      })
    })
    return
  }
  const r = n.round!
  const kind = n.kind as Followup['kind']
  const itemKey = kind === 'repeat' ? `${r.id}:${r.repeat_of!.id}` : kind === 'stopped' ? stoppedKey(r) : String(r.id)
  void mutate<Followup>({ label: `${KIND_WORDS[n.kind]}: ${n.roundName}`, method: 'POST', path: '/api/admin/followups', body: { business_id: r.id, kind, item_key: itemKey, outcome, title: followupTitle(n) } }).then((ack) => {
    if (!ack.ok) { fail(ack); return }
    if (gen !== routeGen) return
    const row = ack.row ?? { id: -1, business_id: r.id, kind, item_key: itemKey, outcome, done_at: '' }
    followups = [row, ...followups.filter((x) => !(x.kind === kind && x.item_key === itemKey))]
    afterAnswer(root, n)
    offerUndo(done, () => {
      if (row.id < 0) { if (gen === routeGen) void load(root); return }
      void mutate({ label: `Put back: ${n.roundName}`, method: 'DELETE', path: `/api/admin/followups/${row.id}`, body: { kind, item_key: itemKey } }).then((back) => {
        if (back.ok) { followups = followups.filter((x) => x.id !== row.id); undone() }
      })
    })
  })
}

// Done's list is out of date after any answer, Undo or Put back: read again, now if it is showing (Sol: an Undo left
// the item in Done).
function doneChanged(): void {
  doneItems = []
  doneNext = null
  doneState = 'idle'
  doneMoreFailed = false
}

// After an answer: the next item takes the place of the one answered.
function afterAnswer(root: HTMLElement, n: Need): void {
  if (openHelp !== null && n.help?.id === openHelp) openHelp = null
  doneChanged()
  redrawNeeds(root)
  root.querySelector<HTMLElement>('.rounds-need button:not([disabled])')?.focus({ preventScroll: true })
}

function needButtons(root: HTMLElement, n: Need, err: HTMLElement): HTMLElement {
  const acts = el('div', { class: 'rounds-need-acts' })
  const answerButton = (label: string, outcome: string): HTMLButtonElement => {
    const b = el('button', { type: 'button', class: 'btn-secondary' }, label)
    b.addEventListener('click', () => answer(root, n, outcome, b, err))
    return b
  }
  if (n.help) {
    const open = el('button', { type: 'button', class: 'btn-primary', 'aria-expanded': String(openHelp === n.help.id) }, openHelp === n.help.id ? 'Close' : 'Open')
    open.addEventListener('click', () => { openHelp = openHelp === n.help!.id ? null : n.help!.id; redrawNeeds(root) })
    acts.append(open, answerButton('Mark replied', 'replied'))
  } else if (n.file) {
    if (n.file.file_gone) acts.append(el('span', { class: 'muted' }, 'File deleted'))
    else acts.append(el('a', { href: `/api/admin/import-requests/${n.file.id}/file`, class: 'btn-primary rounds-link-button', download: n.file.file_name }, 'Download'))
    acts.append(answerButton('Mark put in', 'put_in'))
  } else if (n.kind === 'repeat') {
    acts.append(answerButton('Same business', 'same_business'), answerButton('Different business', 'different_business'))
  } else if (n.kind === 'delete') {
    const email = ownerEmail(n.roundId)
    if (email) acts.append(el('a', { href: `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent('Deleting your Roundly round')}`, class: 'btn-primary rounds-link-button' }, 'Email them'))
  } else {
    const email = ownerEmail(n.roundId)
    if (email) acts.append(el('a', { href: `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent('Roundly')}`, class: 'btn-primary rounds-link-button' }, 'Email them'))
    acts.append(answerButton('Mark emailed', 'emailed'), answerButton('Not needed', 'not_needed'))
  }
  return acts
}

// A Help question opened in place: who asked and when, their words, what they added, the screenshots, and Reply.
function helpDetail(q: HelpQuestionRow): HTMLElement {
  const box = el('div', { class: 'rounds-need-detail' })
  const due = replyByLine(q)
  box.append(el('p', { class: 'muted' }, [`From ${q.asked_by ?? 'someone'} (${q.role})`, `asked ${dateLabel(q.sent_at ?? q.created_at)}`, due?.text ?? ''].filter(Boolean).join(' · ')))
  box.append(el('p', {}, `“${q.question}”`))
  if (q.detail) box.append(el('p', {}, el('strong', {}, 'They added: '), `“${q.detail}”`))
  // Screenshots (0067): each opens full size in a new tab; only this admin account can open them.
  if (q.screenshots) {
    const shots = el('div', { class: 'mb-help-shots' })
    for (let n = 1; n <= Math.min(3, q.screenshots); n++) {
      const href = `/api/admin/help-questions/${q.id}/screenshots/${n}`
      shots.append(el('a', { href, target: '_blank', rel: 'noopener', 'aria-label': `Screenshot ${n}` }, el('img', { src: href, alt: `Screenshot ${n}`, loading: 'lazy' })))
    }
    box.append(shots)
  }
  if (q.asked_by) box.append(el('div', { class: 'rounds-need-acts' }, el('a', { class: 'btn-primary rounds-link-button', href: `mailto:${encodeURIComponent(q.asked_by)}?subject=${encodeURIComponent('Your Roundly question')}` }, 'Reply by email')))
  return box
}

function renderNeed(root: HTMLElement, n: Need): HTMLElement {
  const item = el('div', { class: 'rounds-need' })
  const err = el('p', { class: 'form-error', role: 'alert' })
  const words = needWords(n)
  const name = el('button', { type: 'button', class: `rounds-name${n.yours ? ' rounds-name-yours' : ''}` }, n.roundName)
  name.addEventListener('click', () => openRound(root, n.roundId))
  const line = el('div', { class: 'rounds-need-line' },
    el('span', { class: 'rounds-need-kind' }, KIND_WORDS[n.kind]),
    el('span', { class: 'rounds-need-text' }, name, ' ', el('strong', {}, words.title), words.detail ? el('span', { class: 'muted' }, ` ${words.detail}`) : null),
    n.dueText ? el('span', { class: `rounds-due rounds-due-${n.urgency}` }, n.dueText) : null,
    el('span', { class: 'rounds-need-age' }, `waiting ${waitedWords(n.since)}`),
    needButtons(root, n, err),
  )
  item.append(line)
  // Said beside Mark replied, before it is pressed (Astra: hidden in the opened question, it was easy to miss).
  if (n.help?.screenshots) item.append(el('p', { class: 'rounds-need-note' }, `Mark replied deletes its ${plural(n.help.screenshots, 'screenshot')} 48 hours later. Undo and Put back keep them until then.`))
  if (n.file && !n.file.file_gone) item.append(el('p', { class: 'rounds-need-note' }, 'Mark put in deletes the file 48 hours later. Put back keeps it until then.'))
  if (n.file?.file_gone) item.append(el('p', { class: 'rounds-need-note' }, 'This file was deleted 48 hours after it was marked put in. Ask the owner to send it again if you need it.'))
  if (n.help && openHelp === n.help.id) item.append(helpDetail(n.help))
  item.append(err)
  return item
}

const HELP_OUTCOME: Record<string, string> = {
  none: 'no answer',
  unsure: 'not sure which answer',
  error: "Help couldn't look it up",
  capped: 'daily limit reached',
  answered: 'answered',
  no_key: 'Help switched off',
}

// The questions Help could not answer that nobody sent to Liam: which Help answers to write next. Quiet, under To do.
function renderUnanswered(root: HTMLElement): HTMLElement | null {
  const list = (helpRows ?? []).filter((q) => !q.sent_to_us)
  if (!list.length) return null
  const box = el('div', { class: 'rounds-unanswered' })
  const toggle = el('button', { type: 'button', class: 'btn-secondary', 'aria-expanded': String(showUnanswered) }, showUnanswered ? 'Hide them' : 'See them')
  toggle.addEventListener('click', () => { showUnanswered = !showUnanswered; redrawNeeds(root) })
  box.append(el('div', { class: 'rounds-unanswered-line' }, el('span', {}, `Help couldn't answer ${plural(list.length, 'question')} that nobody sent you. They show which Help answers to write next.`), toggle))
  if (showUnanswered) {
    const ul = el('ul', { class: 'rounds-unanswered-list' })
    for (const q of list) {
      const why = [HELP_OUTCOME[q.outcome] ?? q.outcome, q.helpful === 0 ? 'said it did not help' : null].filter(Boolean).join(' · ')
      ul.append(el('li', {}, el('span', {}, `“${q.question}”`), el('span', { class: 'muted' }, ` ${q.business_name} · ${q.role} · ${shortDate(q.created_at)} · ${why}${q.answer_id ? ` · ${q.outcome === 'answered' ? 'shown' : 'closest guess'}: ${q.answer_id}` : ''}`)))
    }
    box.append(ul)
  }
  return box
}

function renderTodo(root: HTMLElement, body: HTMLElement, all: Need[]): void {
  if (helpRows === null || fileRows === null) {
    if (!helpFailed && !filesFailed) body.append(el('p', { class: 'muted' }, 'Loading…'))
  }
  if (helpFailed) body.append(el('p', { class: 'muted' }, "Couldn't load the Help questions right now."))
  if (filesFailed) body.append(el('p', { class: 'muted' }, "Couldn't load the customer files right now."))
  const matchKind = (n: Need, k: typeof needsKind): boolean => (needsRound === null || n.roundId === needsRound) && (k === 'all' || (k === 'yours' ? n.yours : n.kind === k))
  if (needsRound !== null) {
    const name = rows.find((r) => r.id === needsRound)?.name ?? 'one round'
    const all = el('button', { type: 'button', class: 'btn-secondary' }, 'Show all')
    all.addEventListener('click', () => { needsRound = null; redrawNeeds(root) })
    body.append(el('div', { class: 'rounds-only' }, el('span', {}, `Showing ${name} only.`), all))
  }
  if (all.length) {
    const chips = el('div', { class: 'rounds-chips', role: 'group', 'aria-label': 'Show in To do' })
    for (const c of KIND_CHIPS) {
      const count = all.filter((n) => matchKind(n, c.value)).length
      if (!count && c.value !== 'all' && needsKind !== c.value) continue
      const on = needsKind === c.value
      const chip = el('button', { type: 'button', class: `rounds-chip${on ? ' rounds-chip-on' : ''}`, 'aria-pressed': String(on) }, `${c.label} `, el('b', {}, String(count)))
      chip.addEventListener('click', () => { needsKind = c.value; needsShown = 5; redrawNeeds(root) })
      chips.append(chip)
    }
    body.append(chips)
  }
  const shown = all.filter((n) => matchKind(n, needsKind))
  const list = el('div', { class: 'rounds-need-list' })
  for (const n of shown.slice(0, needsShown)) list.append(renderNeed(root, n))
  body.append(list)
  if (!shown.length && helpRows !== null && fileRows !== null) body.append(el('p', { class: 'rounds-nothing' }, all.length ? 'Nothing of this kind waiting.' : 'Nothing waiting for you.'))
  if (shown.length > 5) {
    const more = el('div', { class: 'rounds-more' })
    if (shown.length > needsShown) {
      const b = el('button', { type: 'button', class: 'btn-secondary' }, `Show ${Math.min(20, shown.length - needsShown)} more`)
      b.addEventListener('click', () => { needsShown += 20; redrawNeeds(root) })
      more.append(b)
    }
    if (needsShown > 5) {
      const b = el('button', { type: 'button', class: 'btn-secondary' }, 'Show fewer')
      b.addEventListener('click', () => { needsShown = 5; redrawNeeds(root) })
      more.append(b)
    }
    body.append(more)
  }
  const unanswered = renderUnanswered(root)
  if (unanswered) body.append(unanswered)
}

// Only the newest request's answer is used (Sol: an older answer arriving late emptied the list).
function loadDone(root: HTMLElement, more = false): void {
  doneState = 'loading'
  doneMoreFailed = false
  const gen = routeGen
  const sequence = ++doneSequence
  const params = new URLSearchParams()
  if (more && doneNext) params.set('before', doneNext)
  if (doneQuery) params.set('q', doneQuery)
  // toString, not .size: older iPhones have no .size, and the page marker and search would be lost (Sonnet).
  const query = params.toString()
  void api<{ items: DoneItem[]; next: string | null }>(`/api/admin/done${query ? `?${query}` : ''}`)
    .then((data) => {
      if (gen !== routeGen || sequence !== doneSequence) return
      doneItems = more ? [...doneItems, ...data.items] : data.items
      doneNext = data.next
      doneState = 'idle'
      redrawDone(root)
    })
    .catch(() => {
      if (gen !== routeGen || sequence !== doneSequence) return
      if (more && doneItems.length) { doneState = 'idle'; doneMoreFailed = true } else doneState = 'failed'
      redrawDone(root)
    })
}

// Put back: the answer undone, and the item on To do again if it is still true.
function putBack(root: HTMLElement, d: DoneItem, button: HTMLButtonElement): void {
  button.disabled = true
  const gen = routeGen
  const send = d.kind === 'help'
    ? mutate({ label: `Help question not replied: ${d.business_name}`, method: 'PATCH', path: `/api/admin/help-questions/${d.id}`, body: { replied: false } })
    : d.kind === 'file'
      ? mutate({ label: `Customer file not put in: ${d.business_name}`, method: 'PATCH', path: `/api/admin/import-requests/${d.id}`, body: { done: false } })
      : mutate({ label: `Put back: ${d.business_name}`, method: 'DELETE', path: `/api/admin/followups/${d.id}`, body: { kind: d.kind, item_key: d.item_key } })
  void send.then((ack) => {
    // Only on the page it was pressed on (Sol: a slow answer drew Rounds over the next page).
    if (gen !== routeGen) return
    if (!ack.ok) {
      let err = button.parentElement?.querySelector<HTMLElement>('.form-error')
      if (!err) { err = el('span', { class: 'form-error', role: 'alert' }); button.after(err) }
      err.textContent = ack.status === 0 ? 'No connection. It will send when you are back online.' : (ack.error ?? "That didn't go back. Try again.")
      if (ack.status !== 0) button.disabled = false
      return
    }
    doneItems = doneItems.filter((x) => !(x.kind === d.kind && x.id === d.id))
    void load(root)
  })
}

// Done's search box is drawn once per opening of the tab; a search's results replace only the list under it, so the box
// is never taken off the page while Liam types (Sol, Sonnet: iPhones drop the keyboard from a box moved and refocused).
const DONE_NOTE = 'Kept for good, newest first. Screenshots and customer files go 48 hours after you deal with them. A round deleted takes its items with it.'
function renderDone(root: HTMLElement, body: HTMLElement): void {
  const search = el('input', { type: 'search', class: 'rounds-search', placeholder: 'Search Done by round, words or email', 'aria-label': 'Search Done', value: doneQuery }) as HTMLInputElement
  search.addEventListener('input', () => {
    if (doneTimer !== null) clearTimeout(doneTimer)
    doneTimer = setTimeout(() => {
      doneQuery = search.value.trim()
      doneItems = []
      doneNext = null
      loadDone(root)
    }, 350) as unknown as number
  })
  const list = el('div', { class: 'rounds-done-list' })
  renderDoneList(root, list)
  body.append(el('div', { class: 'rounds-done-tools' }, search, el('span', { class: 'muted' }, DONE_NOTE)), list)
}

// Done's answer arrived: the list under the search box, or the whole section when Done is not drawn yet.
function redrawDone(root: HTMLElement): void {
  const list = root.querySelector<HTMLElement>('.rounds-needs .rounds-done-list')
  if (needsTab !== 'done' || !list) { redrawNeeds(root); return }
  const fresh = el('div', { class: 'rounds-done-list' })
  renderDoneList(root, fresh)
  list.replaceWith(fresh)
  root.querySelector('.rounds-needs-body')?.setAttribute('aria-busy', String(doneState === 'loading'))
}

function renderDoneList(root: HTMLElement, body: HTMLElement): void {
  if (doneState === 'failed') {
    const again = el('button', { type: 'button', class: 'btn-secondary' }, 'Try again')
    again.addEventListener('click', () => loadDone(root))
    body.append(el('div', { class: 'rounds-more' }, el('p', { class: 'muted' }, "Couldn't load Done right now."), again))
    return
  }
  if (!doneItems.length) {
    body.append(el('p', { class: 'rounds-nothing' }, doneState === 'loading' ? 'Loading…' : doneQuery ? `Nothing in Done matches “${doneQuery}”.` : 'Nothing done yet. What you deal with in To do comes here.'))
    return
  }
  let month = ''
  const list = el('div', { class: 'rounds-need-list' })
  for (const d of doneItems) {
    const m = new Date(savedTime(d.done_at)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'Europe/London' })
    if (m !== month) { month = m; list.append(el('h4', { class: 'rounds-done-month' }, m)) }
    const text = d.kind === 'help' ? `${d.business_name}: “${d.text}”` : d.kind === 'file' ? `${d.business_name} sent ${d.text}` : d.text
    const back = el('button', { type: 'button', class: 'btn-secondary' }, 'Put back')
    back.addEventListener('click', () => putBack(root, d, back))
    list.append(el('div', { class: 'rounds-need' }, el('div', { class: 'rounds-need-line' },
      el('span', { class: 'rounds-need-kind' }, KIND_WORDS[d.kind]),
      el('span', { class: 'rounds-need-text' }, el('strong', {}, text), d.who ? el('span', { class: 'muted' }, ` ${d.who}`) : null),
      el('span', { class: 'rounds-outcome' }, `${OUTCOME_WORDS[d.outcome] ?? d.outcome} ${shortDate(d.done_at)}`),
      el('div', { class: 'rounds-need-acts' }, back),
    )))
  }
  body.append(list)
  if (doneNext) {
    const older = el('button', { type: 'button', class: 'btn-secondary' }, doneMoreFailed ? 'Try again' : 'Show older')
    older.addEventListener('click', () => { older.disabled = true; loadDone(root, true) })
    body.append(el('div', { class: 'rounds-more' }, doneMoreFailed ? el('p', { class: 'muted' }, "Couldn't load the older ones.") : null, older))
  }
  body.append(el('p', { class: 'muted rounds-done-foot' }, "Put back returns it to To do. Screenshots and customer files don't come back once 48 hours have gone."))
}

function needsSection(root: HTMLElement): HTMLElement {
  const section = el('section', { class: 'rounds-needs', 'aria-labelledby': 'rounds-needs-h' })
  const all = buildNeeds()
  const tabs = el('div', { class: 'rounds-tabs', role: 'tablist', 'aria-label': 'Needs you' })
  for (const [tab, label] of [['todo', 'To do'], ['done', 'Done']] as const) {
    const on = needsTab === tab
    const b = el('button', { type: 'button', role: 'tab', id: `rounds-tab-${tab}`, class: 'rounds-tab', 'aria-selected': String(on), 'aria-controls': 'rounds-needs-panel', tabindex: on ? '0' : '-1' }, label, tab === 'todo' ? el('b', {}, ` ${all.length}`) : null)
    b.addEventListener('click', () => {
      needsTab = tab
      if (tab === 'done' && !doneItems.length && doneState !== 'loading') loadDone(root)
      redrawNeeds(root)
      root.querySelector<HTMLElement>('.rounds-tab[aria-selected="true"]')?.focus()
    })
    // The arrows move between the two tabs, as tabs do.
    b.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      e.preventDefault()
      needsTab = needsTab === 'todo' ? 'done' : 'todo'
      if (needsTab === 'done' && !doneItems.length && doneState === 'idle') loadDone(root)
      redrawNeeds(root)
      root.querySelector<HTMLElement>('.rounds-tab[aria-selected="true"]')?.focus()
    })
    tabs.append(b)
  }
  section.append(el('div', { class: 'rounds-needs-head' },
    el('div', {}, el('h3', { id: 'rounds-needs-h' }, 'Needs you'), el('p', { class: 'muted' }, 'Late ones first, then the longest waiting.')),
    tabs))
  const body = el('div', { class: 'rounds-needs-body', role: 'tabpanel', id: 'rounds-needs-panel', 'aria-labelledby': `rounds-tab-${needsTab}`, 'aria-busy': String(needsTab === 'done' && doneState === 'loading') })
  if (needsTab === 'todo') renderTodo(root, body, all)
  else renderDone(root, body)
  section.append(body)
  return section
}

function redrawNeeds(root: HTMLElement): void {
  const old = root.querySelector('.rounds-needs')
  if (old) old.replaceWith(needsSection(root))
}

// ---- the rounds -------------------------------------------------------------------------------------------------

const GOING_FILTERS: { value: RoundsFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New this week' },
  { value: 'every', label: 'Every week' },
  { value: 'some', label: 'Some weeks' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'not-started', label: 'Not started' },
]
const FREE_FILTERS: { value: RoundsFilter; label: string }[] = [
  { value: 'free', label: 'On free months' },
  { value: 'ending', label: 'Ending in 30 days' },
  { value: 'first-100', label: 'First 100' },
  { value: 'tester', label: 'Testers' },
  { value: 'referred', label: 'Referred' },
  { value: 'paused', label: 'Paused or closed' },
]
const SORTS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest sign-up' },
  { value: 'last-job', label: 'Last job done' },
  { value: 'free-ending', label: 'Free time ending soonest' },
  { value: 'most-active', label: 'Most active customers' },
]

function matches(row: AdminBusinessRow, f: RoundsFilter, today: string): boolean {
  if (f === 'all') return true
  if (f === 'free') return onFreeMonths(row, today)
  // A First 100 place is kept as a tester too, as the number at the top counts it.
  if (f === 'first-100') return !!row.first_100_place
  if (f === 'tester') return row.free_offer === 'tester'
  if (f === 'referred') return row.free_offer === 'referred'
  if (f === 'ending') return endingSoon(row, today)
  if (f === 'paused') return row.status === 'suspended' || row.status === 'closed'
  return going(row, today) === f
}

function matchesQuery(row: AdminBusinessRow, q: string): boolean {
  if (!q) return true
  const want = q.toLowerCase()
  return row.name.toLowerCase().includes(want) || row.people.some((p) => p.email.toLowerCase().includes(want))
}

function sorted(list: AdminBusinessRow[]): AdminBusinessRow[] {
  if (sort === 'newest') return list
  const by = [...list]
  if (sort === 'last-job') by.sort((a, b) => (b.last_job_done_on ?? '').localeCompare(a.last_job_done_on ?? ''))
  else if (sort === 'free-ending') by.sort((a, b) => (a.status === 'trial' && a.trial_ends_on ? a.trial_ends_on : '9999').localeCompare(b.status === 'trial' && b.trial_ends_on ? b.trial_ends_on : '9999'))
  else by.sort((a, b) => (b.active_customers ?? 0) - (a.active_customers ?? 0))
  return by
}

// The free time in words: "Free until 12 Apr 2027 · 179 days left", "Free months ended 3 Jan 2027".
function freeLine(row: AdminBusinessRow): string {
  if (!row.trial_ends_on || row.status !== 'trial') return ''
  const left = daysFrom(todayISO(), row.trial_ends_on)
  if (Number.isNaN(left)) return ''
  if (left < 0) return `Free months ended ${dateLabel(row.trial_ends_on)}`
  return `Free until ${dateLabel(row.trial_ends_on)} · ${left === 0 ? 'last day today' : `${plural(left, 'day')} left`}`
}

export function repeatWords(r: NonNullable<AdminBusinessRow['repeat_of']>): string {
  const why = r.why.map((w) => (w === 'name' ? 'the same business name' : w === 'phone' ? 'the same phone number' : `${r.shared} of the same customer addresses`))
  const list = why.length > 1 ? `${why.slice(0, -1).join(', ')} and ${why[why.length - 1]}` : why[0]
  return `may be ${r.name} again: ${list}. Free months are once per business; you decide.`
}

function tradeWords(row: AdminBusinessRow): string {
  return TRADE_OPTIONS.find((t) => t.value === row.trade)?.label ?? 'Window cleaning'
}

function renderFilterRow(root: HTMLElement, label: string, filters: { value: RoundsFilter; label: string }[], outside: AdminBusinessRow[], today: string): HTMLElement {
  const row = el('div', { class: 'rounds-filter-row' }, el('span', { class: 'rounds-filter-label' }, label))
  const chips = el('div', { class: 'rounds-chips', role: 'group', 'aria-label': label })
  for (const f of filters) {
    const on = filter === f.value
    const chip = el('button', { type: 'button', class: `rounds-chip${on ? ' rounds-chip-on' : ''}`, 'aria-pressed': String(on) }, `${f.label} `, el('b', {}, String(outside.filter((r) => matches(r, f.value, today)).length)))
    chip.addEventListener('click', () => { filter = f.value; roundsShown = 25; redrawRounds(root) })
    chips.append(chip)
  }
  row.append(chips)
  return row
}

function renderTable(root: HTMLElement, list: AdminBusinessRow[], today: string): HTMLElement {
  const table = el('table', { class: 'rounds-table' })
  const headings = ['Round', "How it's going", 'Signed up', 'Free until', 'Active customers', 'Jobs done, 7 days', 'Last job done', 'Would pay']
  table.append(el('thead', {}, el('tr', {}, ...headings.map((h, i) => el('th', { scope: 'col', class: i >= 4 && i !== 6 ? 'rounds-num' : '' }, h)))))
  const body = el('tbody', {})
  for (const r of list) {
    const name = el('button', { type: 'button', class: 'rounds-name', 'aria-expanded': String(selected === r.id), 'data-round': String(r.id) }, r.name)
    name.addEventListener('click', () => openRound(root, r.id))
    const free = r.status === 'trial' && r.trial_ends_on ? shortDate(r.trial_ends_on) : STATUS_LABEL[r.status]
    body.append(el('tr', { class: selected === r.id ? 'rounds-row-on' : '' },
      el('td', { 'data-label': '' }, name, el('div', { class: 'muted' }, `${tradeWords(r)} · ${r.country ?? 'GB'}`)),
      el('td', { 'data-label': "How it's going" }, goingTag(r, today)),
      el('td', { 'data-label': 'Signed up' }, shortDate(r.signed_up_on)),
      el('td', { 'data-label': 'Free until' }, free, r.free_offer ? el('div', { class: 'muted' }, FREE_OFFER_WORDS[r.free_offer]) : null),
      el('td', { 'data-label': 'Active customers', class: 'rounds-num' }, String(r.active_customers ?? 0)),
      el('td', { 'data-label': 'Jobs done, 7 days', class: 'rounds-num' }, String(r.jobs_done_7d ?? 0)),
      el('td', { 'data-label': 'Last job done' }, r.last_job_done_on ? shortDate(r.last_job_done_on) : 'None yet'),
      el('td', { 'data-label': 'Would pay', class: 'rounds-num' }, wouldPay(r).words),
    ))
  }
  table.append(body)
  return el('div', { class: 'rounds-table-box' }, table)
}

function renderYours(root: HTMLElement): HTMLElement | null {
  const own = rows.filter(isOwn)
  if (!own.length) return null
  const box = el('details', { class: 'rounds-yours' })
  if (rows.every(isOwn) || (selected !== null && isOwnId(selected))) box.setAttribute('open', '')
  box.append(el('summary', {}, `Yours: ${plural(own.length, 'round')}, left out of every number above`))
  for (const r of own) {
    const name = el('button', { type: 'button', class: 'rounds-name', 'aria-expanded': String(selected === r.id), 'data-round': String(r.id) }, r.name)
    name.addEventListener('click', () => openRound(root, r.id))
    box.append(el('div', { class: `rounds-yours-row${selected === r.id ? ' rounds-row-on' : ''}` },
      name,
      el('span', { class: 'rounds-tag rounds-tag-own' }, r.id === APPLE_REVIEW_ROUND ? "Apple's review round" : 'Yours'),
      el('span', { class: 'muted' }, r.id === APPLE_REVIEW_ROUND
        ? 'Made-up round Apple signs in with. Leave it alone.'
        : `${plural(r.counts.customers, 'customer')} · ${r.jobs_done_7d ?? 0} jobs done in 7 days`),
    ))
  }
  return box
}

function openRound(root: HTMLElement, id: number): void {
  if (selected === null) scrollBeforeOpen = window.scrollY
  selected = id
  pendingChange = null
  redrawRounds(root)
  const panel = root.querySelector<HTMLElement>('.rounds-panel')
  // On a phone or an upright tablet the round fills the screen (CSS), so start it at the top; on a computer it sits beside
  // the list.
  if (window.matchMedia(FULL_SCREEN_ROUND).matches) window.scrollTo({ top: 0 })
  else panel?.scrollIntoView({ block: 'nearest' })
  panel?.querySelector<HTMLElement>('h3')?.focus()
}

// Back where Liam was: the same place on the page (a phone) and the round's own name, ready for the next (Sonnet).
function closeRound(root: HTMLElement): void {
  const was = selected
  selected = null
  pendingChange = null
  redrawRounds(root)
  if (window.matchMedia(FULL_SCREEN_ROUND).matches) window.scrollTo({ top: scrollBeforeOpen })
  if (was !== null) root.querySelector<HTMLElement>(`.rounds-name[data-round="${was}"]`)?.focus({ preventScroll: true })
}
// The width up to which an open round fills the screen with Back, instead of sitting beside the list (rounds.css).
const FULL_SCREEN_ROUND = '(max-width: 1100px)'

function renderPanel(root: HTMLElement, row: AdminBusinessRow): HTMLElement {
  const today = todayISO()
  const panel = el('aside', { class: 'rounds-panel', 'aria-label': row.name })
  const back = el('button', { type: 'button', class: 'rounds-back' }, '‹ Rounds')
  back.addEventListener('click', () => closeRound(root))
  const close = el('button', { type: 'button', class: 'rounds-close', 'aria-label': 'Close' }, '×')
  close.addEventListener('click', () => closeRound(root))
  const tags = el('div', { class: 'rounds-panel-tags' },
    isOwn(row) ? null : goingTag(row, today),
    el('span', { class: `mb-badge mb-badge-status-${row.status}` }, STATUS_LABEL[row.status]),
    row.free_offer ? el('span', { class: 'mb-badge rounds-offer' }, `${FREE_OFFER_WORDS[row.free_offer]} · ${FREE_MONTHS[row.free_offer]} months`) : null,
    isOwn(row) ? el('span', { class: 'mb-badge rounds-own' }, row.id === APPLE_REVIEW_ROUND ? "Apple's review round" : 'Yours') : null,
    el('span', { class: 'mb-badge' }, row.country ?? 'GB'))
  const h = el('h3', { tabindex: '-1' }, row.name)
  panel.append(back, el('div', { class: 'rounds-panel-head' }, el('div', {}, h, tags), close))
  const free = freeLine(row)
  if (free) panel.append(el('p', { class: `rounds-free${endingSoon(row, today) ? ' rounds-free-soon' : ''}` }, free))
  panel.append(el('p', { class: 'muted' }, [tradeWords(row), cameFrom(row)].filter(Boolean).join(' · ')))
  // Free months once per business: a round that may be an earlier one again, and why. Liam decides; nothing changes.
  if (row.repeat_of) panel.append(el('div', { class: 'rounds-repeat' }, `Maybe ${repeatWords(row.repeat_of).replace(/^may be /, '')}`))
  const fact = (label: string, value: string): HTMLElement =>
    el('div', { class: 'rounds-fact' }, el('span', { class: 'rounds-fact-label' }, label), el('span', { class: 'rounds-fact-value' }, value))
  panel.append(el('div', { class: 'rounds-facts' },
    fact('Customers', String(row.counts.customers)),
    fact('Active customers', String(row.active_customers ?? 0)),
    fact('Visits', String(row.counts.visits)),
    fact('Jobs done, 7 days', String(row.jobs_done_7d ?? 0)),
    // Of the last four weeks, how many had work saved (Sonnet: "in a row" is more than the count can say).
    fact('Weeks with work saved', `${row.use?.weeks ?? 0} of the last 4`),
    fact('Days rostered', String(row.counts.days_rostered)),
    fact('Last job done', row.last_job_done_on ? shortDate(row.last_job_done_on) : 'None yet'),
    fact('Last signed in', shortDate(row.last_seen_on)),
    fact('Signed up', shortDate(row.signed_up_on)),
  ))
  panel.append(el('p', { class: 'rounds-people' }, row.people.length ? row.people.map((p) => `${p.email} (${p.role})`).join(' · ') : 'No one signed in yet.'))
  // What it would pay (the price list's band), never a date: charging is Liam's to switch on (Astra).
  if (!isOwn(row)) {
    const pay = wouldPay(row)
    const words = row.country && row.country !== 'GB'
      ? `Not charged: Roundly only charges UK rounds.`
      : !working(row) ? `${STATUS_LABEL[row.status]}: nothing to pay.`
        : `Charging is off. At today's size they would pay ${poundsWords(bandFor(row.active_customers ?? 0).pence)} a month${pay.later ? ', once their free year or tester time ends' : ''}.`
    panel.append(el('p', { class: 'rounds-pay' }, words))
  }
  const waiting = (helpRows ?? []).filter((q) => q.business_id === row.id && q.sent_to_us && !q.replied_at).length
  if (waiting) {
    const go = el('button', { type: 'button', class: 'btn-secondary' }, `Their Help questions waiting (${waiting})`)
    // Just this round's questions, with the round closed first: on a phone Needs you is hidden while a round is open
    // (Sol and Sonnet: the button seemed to do nothing).
    go.addEventListener('click', () => {
      needsTab = 'todo'
      needsKind = 'help'
      needsRound = row.id
      needsShown = 25
      closeRound(root)
      redrawNeeds(root)
      root.querySelector<HTMLElement>('.rounds-needs')?.scrollIntoView({ block: 'start' })
    })
    panel.append(go)
  }
  // The switches, under their own heading; each only where it applies (Sonnet: tester on trial rounds, partner on rounds
  // with free months).
  const settings = el('div', { class: 'rounds-settings' }, el('h4', {}, 'Settings for this round'))
  settings.append(renderStatusControl(row, root))
  for (const c of [renderTester(row, root), renderPartner(row, root), renderSeveralJobs(row, root), renderBroughtMoney(row, root), renderRoute(row, root)]) if (c) settings.append(c)
  panel.append(settings)
  return panel
}

function roundsMain(root: HTMLElement): HTMLElement {
  const today = todayISO()
  const outside = rows.filter((r) => !isOwn(r))
  const main = el('div', { class: 'rounds-main' })
  const col = el('div', { class: 'rounds-list-col' })
  if (outside.length) {
    col.append(
      renderFilterRow(root, "How it's going", GOING_FILTERS, outside, today),
      renderFilterRow(root, 'Free time', FREE_FILTERS, outside, today),
    )
    const search = el('input', { type: 'search', class: 'rounds-search', placeholder: 'Find a round or email', 'aria-label': 'Find a round or email', value: query }) as HTMLInputElement
    search.addEventListener('input', () => {
      query = search.value.trim()
      roundsShown = 25
      const box = root.querySelector<HTMLElement>('.rounds-list-body')
      if (box) fillList(root, box)
    })
    const sortSelect = el('select', { 'aria-label': 'Sort rounds' }) as HTMLSelectElement
    for (const s of SORTS) sortSelect.append(el('option', { value: s.value }, s.label))
    sortSelect.value = sort
    sortSelect.addEventListener('change', () => { sort = sortSelect.value as SortKey; redrawRounds(root) })
    const sortWrap = el('label', { class: 'rounds-sort' }, el('span', {}, 'Sort'), sortSelect)
    col.append(el('div', { class: 'rounds-tools' }, search, sortWrap))
    styleSelect(sortSelect)
    const body = el('div', { class: 'rounds-list-body' })
    fillList(root, body)
    col.append(body)
  } else {
    col.append(el('p', { class: 'rounds-nothing' }, 'No outside rounds yet.'))
  }
  const yours = renderYours(root)
  if (yours) col.append(yours)
  main.append(col)
  const row = selected !== null ? rows.find((r) => r.id === selected) : undefined
  if (row) main.append(renderPanel(root, row))
  return main
}

function fillList(root: HTMLElement, box: HTMLElement): void {
  const today = todayISO()
  const shown = sorted(rows.filter((r) => !isOwn(r) && matches(r, filter, today) && matchesQuery(r, query)))
  clear(box)
  if (!shown.length) {
    box.append(el('p', { class: 'rounds-nothing' }, query ? `No round or email matches “${query}”.` : 'No rounds here.'))
    return
  }
  box.append(renderTable(root, shown.slice(0, roundsShown), today))
  box.append(el('p', { class: 'muted rounds-count' }, shown.length > roundsShown ? `Showing ${roundsShown} of ${shown.length}.` : `Showing all ${shown.length}.`))
  if (shown.length > roundsShown) {
    const more = el('button', { type: 'button', class: 'btn-secondary' }, `Show ${Math.min(25, shown.length - roundsShown)} more`)
    more.addEventListener('click', () => { roundsShown += 25; fillList(root, box) })
    box.append(el('div', { class: 'rounds-more' }, more))
  }
}

function redrawRounds(root: HTMLElement): void {
  root.querySelector('.rounds-page')?.classList.toggle('rounds-detail-open', selected !== null && rows.some((r) => r.id === selected))
  const old = root.querySelector('.rounds-main')
  if (old) old.replaceWith(roundsMain(root))
}

// ---- the switches -----------------------------------------------------------------------------------------------

function renderStatusControl(row: AdminBusinessRow, root: HTMLElement): HTMLElement {
  const wrap = el('div', { class: 'mb-status-wrap rounds-setting' })

  if (typeof row.version !== 'number') {
    // Defensive: PATCH requires a version to guard against a stale write. Show the status as a label rather than offer
    // a control that would always be refused.
    wrap.append(el('span', { class: `mb-badge mb-badge-status-${row.status}` }, STATUS_LABEL[row.status]))
    wrap.append(el('p', { class: 'mb-row-note' }, "Can't be changed from here yet."))
    return wrap
  }

  const version = row.version

  if (pendingChange && pendingChange.id === row.id) {
    const next = pendingChange.status
    const box = el('div', { class: 'mb-confirm rounds-confirm', role: 'alertdialog', 'aria-label': 'Confirm status' })
    const err = el('p', { class: 'form-error', role: 'alert' })
    box.append(el('p', {}, STATUS_ASK[next](row.name)))
    const yes = el('button', { type: 'button', class: 'btn-primary' }, `Change to ${STATUS_LABEL[next]}`)
    const cancel = el('button', { type: 'button', class: 'btn-secondary' }, `Keep ${STATUS_LABEL[row.status]}`)
    cancel.addEventListener('click', () => {
      pendingChange = null
      redrawRounds(root)
    })
    yes.addEventListener('click', () => {
      yes.setAttribute('disabled', 'disabled')
      cancel.setAttribute('disabled', 'disabled')
      void mutate({
        label: `Set ${row.name} to ${STATUS_LABEL[next]}`,
        method: 'PATCH',
        path: `/api/admin/businesses/${row.id}`,
        body: { status: next, version },
      }).then((ack) => {
        if (ack.ok) {
          pendingChange = null
          void load(root)
          return
        }
        yes.removeAttribute('disabled')
        cancel.removeAttribute('disabled')
        showFailure(ack, err, root)
      })
    })
    box.append(el('div', { class: 'mb-confirm-acts' }, yes, cancel), err)
    wrap.append(box)
    queueMicrotask(() => cancel.focus())
    return wrap
  }

  const select = el('select', { 'aria-label': `Status for ${row.name}` }) as HTMLSelectElement
  for (const s of STATUS_VALUES) select.append(el('option', { value: s }, STATUS_LABEL[s]))
  select.value = row.status
  select.addEventListener('change', () => {
    const next = select.value as BusinessStatus
    if (next === row.status) return
    pendingChange = { id: row.id, status: next }
    redrawRounds(root)
  })
  wrap.append(el('span', {}, 'Status'), select)
  styleSelect(select)
  return wrap
}

// A partner's own round (billing stage 8b; partner terms, "Your free year"): Liam links the partner's code (as in their
// link, roundly.work/r/<code>) to the round they run. The round gets a year free once the first business that signed up
// through that code has paid (30 days settled); then the link stays. Rounds without free months show nothing.
function renderPartner(row: AdminBusinessRow, root: HTMLElement): HTMLElement | null {
  if (!row.free_offer) return null
  const wrap = el('div', { class: 'mb-row-sub rounds-partner rounds-setting' })
  const p = row.partner
  if (p?.given_at) {
    wrap.append(el('span', {}, `Partner's own round (${p.code}): free year given, until ${dateLabel(p.free_until)}`))
    return wrap
  }
  const err = el('p', { class: 'form-error', role: 'alert' })
  const save = (code: string | null, button: HTMLElement): void => {
    button.setAttribute('disabled', 'disabled')
    err.textContent = ''
    void mutate({ label: `Partner link: ${row.name}`, method: 'PATCH', path: `/api/admin/businesses/${row.id}/partner`, body: { code } }).then((ack) => {
      if (ack.ok) { void load(root); return }
      button.removeAttribute('disabled')
      err.textContent = ack.error ?? "That didn't save."
    })
  }
  if (p) {
    const off = el('button', { type: 'button', class: 'btn-secondary rounds-switch' }, 'Unlink')
    off.addEventListener('click', () => save(null, off))
    wrap.append(el('span', {}, `Partner's own round (${p.code}): free year once their first referral has paid`), ' ', off, err)
    return wrap
  }
  const input = el('input', { type: 'text', id: `partner-code-${row.id}`, class: 'rounds-partner-code', placeholder: 'Partner code', 'aria-label': `Partner code for ${row.name}`, maxlength: '42' }) as HTMLInputElement
  const link = el('button', { type: 'button', class: 'btn-secondary rounds-switch' }, 'Link partner')
  link.addEventListener('click', () => { if (input.value.trim()) save(input.value.trim(), link) })
  wrap.append(el('span', {}, "Partner's own round:"), ' ', input, ' ', link, err)
  return wrap
}

// An invited tester (0066; price list 12 Oct 2026: about 10 businesses, 12 months free from sign-up). Only Liam marks
// one, here. Taking it off puts back what the round's sign-up link gives. Rounds without free months show nothing.
function renderTester(row: AdminBusinessRow, root: HTMLElement): HTMLElement | null {
  if (!row.free_offer || row.status !== 'trial' || typeof row.version !== 'number') return null
  const version = row.version
  const on = row.free_offer === 'tester'
  const wrap = el('div', { class: 'mb-row-sub mb-tester rounds-setting' })
  wrap.append(el('span', {}, on ? 'Tester: yes, 12 months free' : 'Tester: no'), ' ')
  const err = el('p', { class: 'form-error', role: 'alert' })
  const flip = el('button', { type: 'button', class: 'btn-secondary rounds-switch' }, on ? 'Not a tester' : 'Mark as tester')
  flip.addEventListener('click', () => {
    const ask = on
      ? `Take ${row.name} off testers? Their free time goes back to what their sign-up link gives.`
      : `Mark ${row.name} as a tester? They get 12 months free from the day they signed up.`
    if (!window.confirm(ask)) return
    flip.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Tester ${on ? 'off' : 'on'}: ${row.name}`,
      method: 'PATCH',
      path: `/api/admin/businesses/${row.id}/tester`,
      body: { tester: !on, version },
    }).then((ack) => {
      if (ack.ok) {
        void load(root)
        return
      }
      flip.removeAttribute('disabled')
      // The server's own reason (no free months, not on them) rather than "someone else changed this".
      if (ack.code === 'no_free_time' || ack.code === 'not_on_trial' || ack.code === 'has_plan' || ack.code === 'card_page_open') err.textContent = ack.error ?? "That didn't save."
      else showFailure(ack, err, root)
    })
  })
  wrap.append(flip, err)
  return wrap
}

// "Several jobs" for this round (0042; Liam Q13): new rounds start with it on, every round from before stays off
// until Liam turns it on here. Once a round has customers with more than one job it stays on, so the switch is only
// offered while it can still go either way.
function renderSeveralJobs(row: AdminBusinessRow, root: HTMLElement): HTMLElement | null {
  const sj = row.several_jobs
  if (!sj || typeof sj.version !== 'number') return null
  const version = sj.version
  const wrap = el('div', { class: 'mb-row-sub mb-several-jobs rounds-setting' })
  wrap.append(el('span', {}, sj.on ? 'Several jobs per customer: on' : 'Several jobs per customer: off'), ' ')
  if (sj.on && sj.in_use) {
    wrap.append(el('span', { class: 'muted' }, '(customers here have more than one job, so it stays on)'))
    return wrap
  }
  const err = el('p', { class: 'form-error', role: 'alert' })
  const flip = el('button', { type: 'button', class: 'btn-secondary rounds-switch' }, sj.on ? 'Turn off' : 'Turn on')
  flip.addEventListener('click', () => {
    if (!sj.on && !window.confirm(`Turn on several jobs for ${row.name}? Their customers can then have Gutters, Windows and other jobs, each with its own price and how often.\n\nYou can't turn it off again once any customer has a second job.`)) return
    flip.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Several jobs ${sj.on ? 'off' : 'on'}: ${row.name}`,
      method: 'PATCH',
      path: `/api/admin/businesses/${row.id}/several-jobs`,
      body: { on: !sj.on, version },
    }).then((ack) => {
      if (ack.ok) {
        void load(root)
        return
      }
      flip.removeAttribute('disabled')
      showFailure(ack, err, root)
    })
  })
  wrap.append(flip, err)
  if (!sj.on) wrap.append(el('span', { class: 'muted rounds-setting-note' }, 'Stays on once a customer has a second job.'))
  return wrap
}

// "Money from Squeegee" for this round (0043), beside several jobs, for Liam's testing: an owner switches it on by
// accepting the money check on a backup import. Once the round holds money brought over it stays on.
function renderBroughtMoney(row: AdminBusinessRow, root: HTMLElement): HTMLElement | null {
  const bm = row.brought_money
  if (!bm || typeof bm.version !== 'number') return null
  const version = bm.version
  const wrap = el('div', { class: 'mb-row-sub mb-brought-money rounds-setting' })
  wrap.append(el('span', {}, bm.on ? 'Money from Squeegee: on' : 'Money from Squeegee: off'), ' ')
  if (bm.on && bm.in_use) {
    wrap.append(el('span', { class: 'muted' }, '(customers here have money brought from Squeegee, so it stays on)'))
    return wrap
  }
  const err = el('p', { class: 'form-error', role: 'alert' })
  const flip = el('button', { type: 'button', class: 'btn-secondary rounds-switch' }, bm.on ? 'Turn off' : 'Turn on')
  flip.addEventListener('click', () => {
    if (!bm.on && !window.confirm(`Turn on money from Squeegee for ${row.name}? Their next backup import can then bring what each customer owes or has in credit.\n\nYou can't turn it off again once any customer has money brought over.`)) return
    flip.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Money from Squeegee ${bm.on ? 'off' : 'on'}: ${row.name}`,
      method: 'PATCH',
      path: `/api/admin/businesses/${row.id}/brought-money`,
      body: { on: !bm.on, version },
    }).then((ack) => {
      if (ack.ok) {
        void load(root)
        return
      }
      flip.removeAttribute('disabled')
      showFailure(ack, err, root)
    })
  })
  wrap.append(flip, err)
  if (!bm.on) wrap.append(el('span', { class: 'muted rounds-setting-note' }, 'Stays on once their Squeegee money is brought in.'))
  return wrap
}

// Where this round's emailed enquiries go by postcode (0077; Liam, 19 Oct 2026: EN, N13, N14 and N21 straight into his
// Enfield round). Only offered when another round shares an owner with this one; the server checks that again.
function renderRoute(row: AdminBusinessRow, root: HTMLElement): HTMLElement | null {
  const rt = row.route
  if (!rt || typeof rt.version !== 'number' || (!rt.choices.length && !rt.to)) return null
  const version = rt.version
  const wrap = el('div', { class: 'mb-row-sub rounds-route rounds-setting' })
  const goesTo = rt.choices.find((c) => c.id === rt.to)?.name ?? rows.find((r) => r.id === rt.to)?.name
  wrap.append(el('span', { class: 'rounds-route-now' }, rt.to && rt.areas
    ? `Emailed enquiries from ${rt.areas.split(',').join(', ')} go to ${goesTo ?? 'another round'}.`
    : 'Emailed enquiries all stay in this round.'))
  const areas = el('input', { type: 'text', class: 'rounds-route-areas', placeholder: 'Postcodes, like EN, N13, N14', 'aria-label': `Postcodes whose enquiries go to another round, for ${row.name}`, value: rt.areas ? rt.areas.split(',').join(', ') : '', maxlength: '200' }) as HTMLInputElement
  const target = el('select', { 'aria-label': `Round those enquiries go to, for ${row.name}` }) as HTMLSelectElement
  target.append(el('option', { value: '' }, 'Nowhere: they stay here'))
  for (const c of rt.choices) target.append(el('option', { value: String(c.id) }, c.name))
  target.value = rt.to ? String(rt.to) : ''
  const save = el('button', { type: 'button', class: 'btn-secondary rounds-switch' }, 'Save')
  const err = el('p', { class: 'form-error', role: 'alert' })
  save.addEventListener('click', () => {
    const to = target.value ? Number(target.value) : null
    const words = areas.value.trim()
    if (to !== null && !words) { err.textContent = 'Say which postcodes go there.'; return }
    save.setAttribute('disabled', 'disabled')
    err.textContent = ''
    void mutate({ label: `Enquiry postcodes: ${row.name}`, method: 'PATCH', path: `/api/admin/businesses/${row.id}/route`, body: { to, areas: to === null ? null : words, version } }).then((ack) => {
      if (ack.ok) { void load(root); return }
      save.removeAttribute('disabled')
      showFailure(ack, err, root)
    })
  })
  wrap.append(el('div', { class: 'rounds-route-form' }, areas, target, save), err)
  styleSelect(target)
  return wrap
}

// ---- the page ---------------------------------------------------------------------------------------------------

// The page sits in its own box inside the screen, so its classes (a round open full screen on a phone) leave the
// shared screen as it was for the next page.
function render(root: HTMLElement): void {
  clear(root)
  const page = el('div', { class: `rounds-page${selected !== null && rows.some((r) => r.id === selected) ? ' rounds-detail-open' : ''}` })
  root.append(page)
  if (rows.length === 0) {
    page.append(el('div', { class: 'rounds-head' }, el('h2', {}, 'Rounds')), el('p', { class: 'empty-note' }, 'No businesses have signed up yet.'))
    return
  }
  page.append(renderHead(), renderSummary(), needsSection(root), roundsMain(root))
}

// Help questions and customer files load beside the rounds: if either fails, the rest of the page stands.
function loadNeeds(root: HTMLElement): void {
  const gen = routeGen
  void api<{ questions: HelpQuestionRow[] }>('/api/admin/help-questions')
    .then((data) => { if (gen !== routeGen) return; helpRows = data.questions; helpFailed = false; redrawNeeds(root); if (selected !== null) redrawRounds(root) })
    .catch(() => { if (gen !== routeGen) return; helpFailed = true; redrawNeeds(root) })
  void api<{ requests: ImportRequestRow[] }>('/api/admin/import-requests')
    .then((data) => { if (gen !== routeGen) return; fileRows = data.requests; filesFailed = false; redrawNeeds(root) })
    .catch(() => { if (gen !== routeGen) return; filesFailed = true; redrawNeeds(root) })
  if (needsTab === 'done') loadDone(root)
}

async function load(root?: HTMLElement): Promise<void> {
  const gen = routeGen
  const sequence = ++requestSequence
  const target = root ?? document.querySelector<HTMLElement>('#screen')!
  try {
    const data = await api<{ businesses: AdminBusinessRow[]; followups?: Followup[] }>('/api/admin/businesses', 'admin-businesses')
    if (gen !== routeGen || sequence !== requestSequence) return
    rows = data.businesses
    followups = data.followups ?? []
    pendingChange = null
    render(target)
    loadNeeds(target)
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    const cached = readCache<{ businesses: AdminBusinessRow[]; followups?: Followup[] }>('admin-businesses')
    if (cached) {
      rows = cached.data.businesses
      followups = cached.data.followups ?? []
      render(target)
      // Needs you tries too, and says so if it cannot load (Sonnet: it said Loading… for ever).
      loadNeeds(target)
      loadError(target, (err as Error).message, () => void load(), cached.at)
    } else {
      clear(target)
      loadError(target, (err as Error).message, () => void load())
    }
  }
}

export function mountCleaners(): void {
  pendingChange = null
  filter = 'all'
  query = ''
  sort = 'newest'
  roundsShown = 25
  selected = null
  needsTab = 'todo'
  needsKind = 'all'
  needsShown = 5
  openHelp = null
  needsRound = null
  showUnanswered = false
  helpRows = null
  fileRows = null
  helpFailed = false
  filesFailed = false
  doneItems = []
  doneNext = null
  doneQuery = ''
  doneState = 'idle'
  void load()
}

export function cleanersOnDataChanged(): void {
  void load()
}
